/**
 * Hot Spot Media
 *
 * Each hotspot button toggles its card. One card open at a time.
 *
 * Desktop (>= @bp-small, 768px): the card is a popover anchored to its dot. Popper
 * positions it and flips/shifts so it never renders off screen. Popper is
 * already in the bundle via Bootstrap, so no extra dependency is needed.
 *
 * Mobile: the card is a stacked panel below the image, so no positioning is
 * applied — CSS handles the layout and Popper is destroyed.
 *
 * Close on: the X button, Escape, or a click outside the card/dot.
 */

import { createPopper } from '@popperjs/core';

// 48rem = @bp-small. Must match the breakpoint in hotspot-media.less and the
// <picture> source in hotspot-media.php.
var DESKTOP_QUERY = '(min-width: 48rem)';

function initBlock(block) {
    var hotspots = Array.prototype.slice.call(block.querySelectorAll('.hsm-hotspot'));
    var cards = Array.prototype.slice.call(block.querySelectorAll('.hsm-card'));

    if (!hotspots.length || !cards.length) {
        return;
    }

    var desktop = window.matchMedia(DESKTOP_QUERY);
    var popper = null;
    var openSpot = null;

    function cardFor(spot) {
        return block.querySelector('#' + CSS.escape(spot.getAttribute('aria-controls')));
    }

    function destroyPopper() {
        if (popper) {
            popper.destroy();
            popper = null;
        }
    }

    function closeAll(refocus) {
        destroyPopper();

        hotspots.forEach(function (spot) {
            spot.setAttribute('aria-expanded', 'false');
            spot.classList.remove('is-active');
        });

        cards.forEach(function (card) {
            card.hidden = true;
            card.classList.remove('is-open');
        });

        if (refocus && openSpot) {
            openSpot.focus();
        }

        openSpot = null;
    }

    function open(spot) {
        var card = cardFor(spot);

        if (!card) {
            return;
        }

        closeAll(false);

        spot.setAttribute('aria-expanded', 'true');
        spot.classList.add('is-active');
        card.hidden = false;
        card.classList.add('is-open');
        openSpot = spot;

        // Only anchor the card to the dot on desktop; on mobile it is a
        // stacked block positioned by CSS.
        if (desktop.matches) {
            popper = createPopper(spot, card, {
                placement: 'top',
                modifiers: [
                    { name: 'offset', options: { offset: [0, 16] } },
                    { name: 'flip', options: { fallbackPlacements: ['bottom', 'right', 'left'] } },
                    { name: 'preventOverflow', options: { padding: 16 } }
                ]
            });
        }
    }

    function toggle(spot) {
        if (spot.getAttribute('aria-expanded') === 'true') {
            closeAll(true);
        } else {
            open(spot);
        }
    }

    hotspots.forEach(function (spot) {
        spot.addEventListener('click', function (event) {
            event.preventDefault();
            event.stopPropagation();
            toggle(spot);
        });
    });

    cards.forEach(function (card) {
        var close = card.querySelector('.hsm-card-close');

        if (close) {
            close.addEventListener('click', function () {
                closeAll(true);
            });
        }

        // Keep clicks inside the card from bubbling to the outside-click handler.
        card.addEventListener('click', function (event) {
            event.stopPropagation();
        });
    });

    document.addEventListener('click', function () {
        if (openSpot) {
            closeAll(false);
        }
    });

    document.addEventListener('keydown', function (event) {
        if (event.key === 'Escape' && openSpot) {
            closeAll(true);
        }
    });

    // Switching breakpoints changes whether the card is a popover or a stacked
    // panel, so drop any open state rather than leaving a stale popper.
    var onChange = function () {
        closeAll(false);
    };

    if (typeof desktop.addEventListener === 'function') {
        desktop.addEventListener('change', onChange);
    } else if (typeof desktop.addListener === 'function') {
        desktop.addListener(onChange);
    }
}

function initHotspotMedia() {
    document.querySelectorAll('.hotspot-media').forEach(initBlock);
}

document.addEventListener('DOMContentLoaded', initHotspotMedia);

export { initHotspotMedia };
